import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import type { HistoricoFaturamentoLocalItem } from "interfaces/loja/pedidoFaturamento";
import { emitirFaturamentoPedidoLoja } from "services/lojaPedidos";
import type { EmitirFaturamentoPedidoResponse } from "interfaces/loja/pedidoFaturamento";
import { labelStatusFaturamento } from "utils/pedidoOperacional";
import { formatDate } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";

type PedidoFaturamentoFormProps = {
  pedidoId: string;
  detalhe: IPedidoDetalhe | null;
  /** Patch opcional vindo do POST (GET ainda pode não refletir todos os campos). */
  onAfterEmitSuccess: (patch?: Partial<IPedidoDetalhe>) => Promise<void>;
};

function pickFaturamento(d: IPedidoDetalhe | null) {
  const fp = d?.faturamentoPedido;
  if (!fp) return null;
  return fp;
}

export default function PedidoFaturamentoForm({
  pedidoId,
  detalhe,
  onAfterEmitSuccess,
}: PedidoFaturamentoFormProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);

  const statusFat = detalhe?.statusFaturamento ?? "PENDENTE";
  const snap = useMemo(() => pickFaturamento(detalhe), [detalhe]);

  const [numeroNota, setNumeroNota] = useState("");
  const [serieNota, setSerieNota] = useState("");
  const [chaveNfe, setChaveNfe] = useState("");
  const [valorNota, setValorNota] = useState<string>("");
  const [xmlUrl, setXmlUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [observacaoFiscal, setObservacaoFiscal] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [historico, setHistorico] = useState<HistoricoFaturamentoLocalItem[]>(
    []
  );

  useEffect(() => {
    const fp = pickFaturamento(detalhe);
    setNumeroNota(fp?.numeroNota?.trim() ?? "");
    setSerieNota(fp?.serieNota?.trim() ?? "");
    setChaveNfe(fp?.chaveNfe?.trim() ?? "");
    setValorNota(
      fp?.valorNota != null && fp.valorNota !== ""
        ? String(fp.valorNota)
        : detalhe?.total != null
          ? String(detalhe.total)
          : ""
    );
    setXmlUrl(fp?.xmlUrl?.trim() ?? "");
    setPdfUrl(fp?.pdfUrl?.trim() ?? "");
    setObservacaoFiscal(fp?.observacaoFiscal?.trim() ?? "");
  }, [detalhe]);

  const podeEmitir = useMemo(() => {
    return numeroNota.trim().length > 0;
  }, [numeroNota]);

  const onSubmit = useCallback(async () => {
    setError(null);
    setSuccess(null);
    if (!podeEmitir) {
      setError("Informe o número da nota.");
      return;
    }
    let valorNum: number | undefined;
    if (valorNota.trim() !== "") {
      const n = Number(valorNota.replace(",", "."));
      if (Number.isNaN(n) || n < 0) {
        setError("Valor da nota inválido.");
        return;
      }
      valorNum = n;
    }

    setSubmitting(true);
    try {
      const { data, status } = await emitirFaturamentoPedidoLoja(pedidoId, {
        numeroNota,
        serieNota: serieNota.trim() || undefined,
        chaveNfe: chaveNfe.trim() || undefined,
        valorNota: valorNum,
        xmlUrl: xmlUrl.trim() || undefined,
        pdfUrl: pdfUrl.trim() || undefined,
        observacaoFiscal: observacaoFiscal.trim() || undefined,
      });

      if (status === 200) {
        const d = data as EmitirFaturamentoPedidoResponse;
        if (d.success === false) {
          setError(d.message ?? "Não foi possível emitir o faturamento.");
          return;
        }
        const msg = d.message ?? "Faturamento emitido com sucesso.";
        setSuccess(msg);
        setHistorico((prev) => [
          {
            id: `h-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            em: new Date().toISOString(),
            descricao: `Emissão registrada — NF ${numeroNota.trim()}${serieNota.trim() ? ` série ${serieNota.trim()}` : ""}.`,
          },
          ...prev,
        ]);
        const patch = d.data
          ? ({
              statusFaturamento: d.data
                .statusFaturamento as IPedidoDetalhe["statusFaturamento"],
              status: d.data.status as IPedidoDetalhe["status"],
              statusSeparacao: d.data
                .statusSeparacao as IPedidoDetalhe["statusSeparacao"],
              statusLogistico: d.data
                .statusLogistico as IPedidoDetalhe["statusLogistico"],
              statusRetirada: d.data
                .statusRetirada as IPedidoDetalhe["statusRetirada"],
            } satisfies Partial<IPedidoDetalhe>)
          : undefined;
        await onAfterEmitSuccess(patch);
      } else {
        const msg =
          (data as { message?: string })?.message ??
          `Não foi possível concluir (HTTP ${status}).`;
        setError(msg);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Falha ao emitir faturamento.");
    } finally {
      setSubmitting(false);
    }
  }, [
    podeEmitir,
    pedidoId,
    numeroNota,
    serieNota,
    chaveNfe,
    valorNota,
    xmlUrl,
    pdfUrl,
    observacaoFiscal,
    onAfterEmitSuccess,
  ]);

  return (
    <Stack spacing={3}>
      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="button" color="text.secondary" fontWeight={600}>
          Status fiscal (pedido)
        </Typography>
        <Chip
          label={labelStatusFaturamento(String(statusFat))}
          size="small"
          color={
            statusFat === "EMITIDO"
              ? "success"
              : statusFat === "ERRO"
                ? "warning"
                : statusFat === "CANCELADO"
                  ? "default"
                  : "info"
          }
          sx={{ fontWeight: 700 }}
        />
      </MDBox>

      {success ? (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <MDBox p={2.5}>
          <Typography
            variant="button"
            fontWeight={700}
            color="primary"
            mb={2}
            display="block"
          >
            Dados da nota fiscal
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="Número da nota *"
                value={numeroNota}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setNumeroNota(e.target.value)
                }
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="Série"
                value={serieNota}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSerieNota(e.target.value)
                }
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="Chave NF-e"
                value={chaveNfe}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setChaveNfe(e.target.value)
                }
                disabled={submitting}
                inputProps={{ maxLength: 60 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="Valor da nota"
                value={valorNota}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setValorNota(e.target.value)
                }
                disabled={submitting}
                placeholder="Ex.: 199.90"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="URL do XML"
                value={xmlUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setXmlUrl(e.target.value)
                }
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="URL do PDF (DANFE)"
                value={pdfUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPdfUrl(e.target.value)
                }
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                multiline
                rows={3}
                label="Observação fiscal"
                value={observacaoFiscal}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setObservacaoFiscal(e.target.value)
                }
                disabled={submitting}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
          </Grid>

          {snap?.dataEmissao ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={2}
            >
              Última emissão registrada em:{" "}
              {formatDate(snap.dataEmissao, "dd/MM/yyyy HH:mm:ss")}
            </Typography>
          ) : null}

          <MDBox
            mt={2}
            display="flex"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <MDButton
              variant="gradient"
              color="dark"
              disabled={submitting || !podeEmitir}
              onClick={() => void onSubmit()}
            >
              {submitting ? "Emitindo…" : "Emitir faturamento"}
            </MDButton>
            <Typography variant="caption" color="text.secondary">
              * Obrigatório. Reenviar substitui os dados (upsert no servidor).
            </Typography>
          </MDBox>
        </MDBox>
      </Card>

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <MDBox p={2.5}>
          <Typography
            variant="button"
            fontWeight={700}
            color="primary"
            mb={1}
            display="block"
          >
            Histórico resumido (esta sessão)
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1.5}
          >
            Linha do tempo local após cada emissão bem-sucedida. Quando a API
            listar histórico no detalhe do pedido, este bloco poderá ser
            alimentado automaticamente.
          </Typography>
          <Divider sx={{ my: 1 }} />
          {historico.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nenhum evento registrado ainda nesta tela.
            </Typography>
          ) : (
            <List dense disablePadding>
              {historico.map((h) => (
                <ListItem key={h.id} alignItems="flex-start" sx={{ py: 1 }}>
                  <ListItemText
                    primary={h.descricao}
                    secondary={formatDate(h.em, "dd/MM/yyyy HH:mm:ss")}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600,
                    }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </MDBox>
      </Card>

      {detalhe?.total != null ? (
        <Typography variant="caption" color="text.secondary">
          Referência: total do pedido {formatCurrency(Number(detalhe.total))}.
        </Typography>
      ) : null}
    </Stack>
  );
}
